<script setup>
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  inject,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { usePipelineCard } from 'dashboard/composables/usePipelineCard';
import { usePipelineDates } from 'dashboard/composables/usePipelineDates';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import OpportunityHistoryModal from './OpportunityHistoryModal.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import {
  getCardOpportunitiesCount,
  getCardOpportunitiesHistoryCount,
  getCardVoidedCount,
  getCardVoidedValue,
  isCardFullyVoided,
  getCardPriority,
  getCardRealizedValue,
  getCardValue,
} from '../helpers/columnFilterMatchers';
import { dismissMenuOnFocusOut } from '../helpers/menuDismissal';
import { formatLocalizedCurrency } from '../utils/localeFormatting';
import { getStageColorClasses, stageColorStyle } from './pipelineTemplates';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
// Card-2B (2026-04-22): vOnClickOutside no template root substitui
// document.addEventListener (100 cards = 100 listeners no document antes).
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  // Nome do stage atual; usado para suprimir labels redundantes com o
  // título da coluna (ex.: card em "Qualificação" não mostra label "qualificado").
  stageName: {
    type: String,
    default: '',
  },
});
const emit = defineEmits([
  'edit',
  'delete',
  'openDetails',
  'dragstart',
  'dragend',
  'statusChange',
  'move',
]);
// Injetar o map de labels fornecido pelo componente pai
const labelsMap = inject('labelsMap', {});
import { emitter } from 'shared/helpers/mitt';

// Drag state
const isDragging = ref(false);

// Title truncation detection (Phase 2 - UX Improvements)
const titleElement = ref(null);

const { t, locale } = useI18n();
const router = useRouter();
const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canDeleteCard } = usePipelinePermissions(selectedPipeline);
// Audit 2026-05-07 — feature flag pipeline_sequences. Quando desligada para
// esta account, o badge de sequência some e nenhum fetch é disparado (evita
// 404s e ruído no console se SuperAdmin desligar a feature em runtime).
const { isCloudFeatureEnabled } = useAccount();
const isSequencesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_SEQUENCES)
);
// Fase 59 — mesma ideia para Oportunidades: sem a cross feature o card volta a
// mostrar o valor PREVISTO e não oferece o histórico de vendas. Os acumulados
// podem até existir na coluna (conta que já teve a feature ligada), mas deixam
// de ser o que essa conta enxerga.
const isOpportunitiesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_OPPORTUNITIES)
);
const { showAlert } = useAlert();

// Status menu
const showStatusMenu = ref(false);
const statusPopoverRef = ref(null);
const statusButtonRef = ref(null);
const statusDropdownRef = ref(null);

// Actions menu
const showActionsMenu = ref(false);
const actionsPopoverRef = ref(null);
const actionsButtonRef = ref(null);
const actionsDropdownRef = ref(null);

// Priority — as opções vivem dentro do menu de ações; só o estado de
// atualização em voo e o container (para navegação por teclado) sobram.
const isUpdatingPriority = ref(false);
const priorityDropdownRef = ref(null);

// Agent dropdown — UX 2026-04-22: trocar agente inline (antes click
// no agente caía pro article e abria detalhes do card sem affordance
// de mudança).
const showAgentDropdown = ref(false);
const agentPopoverRef = ref(null);
const agentButtonRef = ref(null);
const agentDropdownRef = ref(null);
const agentSearchRef = ref(null);
const agentListRef = ref(null);
const agentSearchQuery = ref('');

const focusElement = elementRef => {
  const element =
    elementRef?.value?.$el ||
    elementRef?.value ||
    elementRef?.$el ||
    elementRef;
  element?.focus?.();
};

const getMenuItems = container => {
  if (!container) return [];
  return [
    ...container.querySelectorAll(
      '[role="menuitem"]:not([disabled]), [role="menuitemradio"]:not([disabled])'
    ),
  ];
};

const focusMenuItem = (menuRef, position = 'first') => {
  const items = getMenuItems(menuRef.value);
  if (!items.length) return;

  let target = items[0];
  if (position === 'last') {
    target = items.at(-1);
  } else if (position === 'selected') {
    target =
      items.find(item => item.getAttribute('aria-checked') === 'true') ||
      target;
  }

  items.forEach(item => {
    item.tabIndex = item === target ? 0 : -1;
  });
  target?.focus();
};

const handleMenuKeydown = (event, triggerRef, closeMenu) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeMenu();
    nextTick(() => focusElement(triggerRef));
    return;
  }

  const items = getMenuItems(event.currentTarget);
  if (!items.length) return;

  const currentIndex = items.indexOf(document.activeElement);
  let targetIndex = null;
  if (event.key === 'ArrowDown') {
    targetIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
  }
  if (event.key === 'ArrowUp') {
    targetIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
  }
  if (event.key === 'Home') targetIndex = 0;
  if (event.key === 'End') targetIndex = items.length - 1;

  if (targetIndex !== null) {
    event.preventDefault();
    event.stopPropagation();
    items.forEach((item, index) => {
      item.tabIndex = index === targetIndex ? 0 : -1;
    });
    items[targetIndex]?.focus();
  }
};

const closeAfterFocusLeaves = (event, closeMenu) => {
  dismissMenuOnFocusOut(event, closeMenu);
};

// Composables
const { hasConversation, isAssigned, displayTitle } = usePipelineCard(
  computed(() => props.card)
);

// Date composable for relative dates
const {
  cardLifetimeShort,
  deadline,
  deadlineBadgeClasses,
  isOverdue,
  scheduledAt,
  hasScheduledDate,
  timeInCurrentStage,
} = usePipelineDates(computed(() => props.card));

// Card status
const cardStatus = computed(() => {
  return props.card.status || 'open';
});

const statusLabel = computed(() => {
  const status = cardStatus.value;
  const statusMap = {
    open: t('PIPELINE.CARD.STATUS.OPEN'),
    won: t('PIPELINE.CARD.STATUS.WON'),
    lost: t('PIPELINE.CARD.STATUS.LOST'),
  };
  return statusMap[status] || t('PIPELINE.CARD.STATUS.OPEN');
});

const statusClasses = computed(() => {
  const status = cardStatus.value;
  const statusStyles = {
    open: 'bg-n-alpha-1 text-n-slate-11 border-n-weak',
    won: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    lost: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
  };
  return statusStyles[status] || statusStyles.open;
});

// ── SLA da etapa ───────────────────────────────────────────────────────────
// A7 (2026-08-11): o backend já detectava e marcava o estouro do SLA
// (Pipelines::SlaCheckerService), mas o card do kanban não mostrava nada. O
// índice passou a mandar `sla_hours` (limite configurado na etapa) e
// `sla_exceeded` (verdade no instante da serialização). O tempo na etapa
// continua sendo derivado no cliente — mesma decisão do P1.3 do índice, que
// tirou `time_in_current_stage` do payload por mudar a cada segundo. O flag do
// servidor é o fallback para quando não há data de entrada nem de criação.
// Não há timer: como qualquer computed, só reavalia quando o card re-renderiza.
const slaHours = computed(() => Number(props.card?.sla_hours) || 0);

const isSlaExceeded = computed(() => {
  // sla_hours = 0 cobre os dois casos de "sem SLA": funil com
  // stage_sla_enabled desligado e etapa sem limite configurado.
  if (slaHours.value <= 0) return false;
  // Card fechado (ganho/perdido) ou arquivado não corre mais contra o SLA.
  if (cardStatus.value !== 'open') return false;

  const elapsed = timeInCurrentStage.value?.seconds;
  if (elapsed != null) return elapsed > slaHours.value * 3600;

  return Boolean(props.card?.sla_exceeded);
});

const slaTooltip = computed(() => {
  if (!isSlaExceeded.value) return '';

  const elapsedText = timeInCurrentStage.value?.text;
  return elapsedText
    ? t('PIPELINE.CARD.SLA_EXCEEDED_TOOLTIP', {
        elapsed: elapsedText,
        hours: slaHours.value,
      })
    : t('PIPELINE.CARD.SLA_EXCEEDED_TOOLTIP_SHORT', { hours: slaHours.value });
});

// Status options for menu
// Verificar se etapas ganho/perdido estão habilitadas
const enableWonLostStages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  return pipeline?.settings?.enable_won_lost_stages !== false;
});

// Status options for menu (filtra status atual e won/lost se desabilitado)
const statusOptions = computed(() => {
  const allOptions = [{ value: 'open', label: t('PIPELINE.CARD.STATUS.OPEN') }];

  // Só adiciona opções de won/lost se habilitado
  if (enableWonLostStages.value) {
    allOptions.push(
      { value: 'won', label: t('PIPELINE.CARD.STATUS.WON') },
      { value: 'lost', label: t('PIPELINE.CARD.STATUS.LOST') }
    );
  }

  // Filtra o status atual para não mostrar opção redundante
  return allOptions.filter(option => option.value !== cardStatus.value);
});

// Determina se o badge de status pode ser clicável (bidirecional: open↔won/lost)
const canChangeStatus = computed(() => {
  return enableWonLostStages.value;
});

// Formatted value for display.
// Ciclo 6 (2026-06-10): paridade com getCardValue (expected_revenue →
// item_details.value), a mesma semântica de filtros/sort/total da coluna.
// Antes lia só item_details.value — cards com valor server-side apenas em
// expected_revenue não mostravam o badge "R$" (achado QA do ciclo 5).
// Zero é um valor legítimo e deve continuar visível; apenas NULL fica oculto.
// Fase 59 — quantas vendas já foram registradas neste card. Acima de zero, o
// card deixa de mostrar o valor previsto e passa a mostrar o REALIZADO.
const opportunitiesCount = computed(() =>
  getCardOpportunitiesCount(props.card)
);
// O acesso ao histórico segue a contagem TOTAL (com estornadas): estornar a
// última venda zera o realizado, mas a trilha de auditoria continua existindo e
// precisa continuar alcançável.
const opportunitiesHistoryCount = computed(() =>
  getCardOpportunitiesHistoryCount(props.card)
);
// NOOVI: o histórico de ciclos NÃO depende mais da flag. O razão virou core, e
// dado gravado que ninguém consegue ver é o mesmo que dado perdido — o contador
// sozinho já diz se há o que mostrar. A camada rica (catálogo, oferta,
// relatório) segue atrás de `isOpportunitiesEnabled`.
const hasOpportunityHistory = computed(
  () => opportunitiesHistoryCount.value > 0
);
const showOpportunityHistory = ref(false);

// O <article> do card é `draggable` e hospeda o modal de histórico dentro de si.
// Enquanto o modal está aberto o card NÃO pode ser arrastável: pressionar e
// mover dentro do modal — para selecionar o motivo que se acabou de digitar, ou
// num gesto impreciso antes de "Estornar venda" — começava a arrastar o CARD, e
// soltar em outra coluna MUDAVA A ETAPA do negócio sem ninguém pedir. É defeito
// de dado com aparência de defeito visual.
//
// Desligar o atributo é o que corta na origem: `dragstart` dispara no elemento
// DRAGGABLE (o próprio <article>), não no descendente onde o gesto começou, então
// um `@dragstart.stop` no invólucro do modal nunca veria o evento — ele não passa
// por lá. O `@click.stop` daquele invólucro resolve o clique e só o clique.
const canDrag = computed(
  () => props.draggable && !showOpportunityHistory.value
);

// Rótulo do chip: descreve o que está ESCRITO nele (a contagem com estornadas),
// não a receita. Antes o texto visível dizia "2 oportunidades" e o tooltip/
// aria-label falavam de receita de 0 registradas — quem usa leitor de tela
// recebia um número diferente do de quem enxerga.
const opportunitiesChipLabel = computed(() =>
  t('PIPELINE.CARD.OPPORTUNITIES_TOOLTIP', opportunitiesHistoryCount.value, {
    named: {
      count: opportunitiesHistoryCount.value,
      recorded: opportunitiesCount.value,
    },
  })
);

const formatCardCurrency = value => {
  const detailsCurrencyValue = props.card?.item_details?.currency;
  const detailsCurrency =
    typeof detailsCurrencyValue === 'string'
      ? { code: detailsCurrencyValue }
      : detailsCurrencyValue;
  const rootCurrency =
    typeof props.card?.currency === 'string' && props.card.currency
      ? { code: props.card.currency }
      : null;
  const currency = rootCurrency ||
    detailsCurrency || {
      code: 'BRL',
      locale: locale.value,
    };
  try {
    return formatLocalizedCurrency(value, {
      locale: currency.locale || locale.value,
      currency: currency.code || 'BRL',
    });
  } catch {
    return `${currency.code || 'BRL'} ${value}`;
  }
};

const formattedRealizedValue = computed(() => {
  if (!isOpportunitiesEnabled.value) return null;

  const value = getCardRealizedValue(props.card);
  return value === null ? null : formatCardCurrency(value);
});

const formattedValue = computed(() => {
  const value = getCardValue(props.card);
  return value === null ? null : formatCardCurrency(value);
});

// O que o card exibe: realizado quando já houve venda, previsto caso contrário.
const displayedValue = computed(
  () => formattedRealizedValue.value ?? formattedValue.value
);

// O número mostrado é RECEITA REALIZADA? Só então ele pode ser verde e abrir o
// histórico. Num card cujas vendas foram todas estornadas ainda existe
// histórico (o chip continua lá), mas o valor exibido volta a ser o PREVISTO —
// pintá-lo de verde e abrir um histórico que soma zero seria mentir sobre o
// dinheiro.
const showsRealizedValue = computed(
  () => displayedValue.value !== null && formattedRealizedValue.value !== null
);

// ── Sinal de estorno no card (E5) ────────────────────────────────────────────
//
// Queixa literal do dono: "não dá para dizer se a venda inteira foi cancelada".
// Estornar recalcula os agregados mas NÃO mexe em `won_at`, `won_value` nem na
// etapa — isso é invariante de projeto e não se toca. O que faltava era
// SINALIZAR: com tudo estornado o card voltava a exibir um número em cor neutra,
// idêntico ao de um card que nunca vendeu.
//
// Os dois casos precisam de sinais DIFERENTES. Um só badge para "tudo cancelado"
// e "uma de três estornada" deixa a pergunta do dono sem resposta — ele continua
// sem saber qual dos dois aconteceu.
const voidedCount = computed(() =>
  isOpportunitiesEnabled.value ? getCardVoidedCount(props.card) : 0
);
const isFullyVoided = computed(
  () => isOpportunitiesEnabled.value && isCardFullyVoided(props.card)
);
const hasPartialVoid = computed(
  () => voidedCount.value > 0 && !isFullyVoided.value
);

// Quanto foi cancelado. Só existe para o card TODO estornado, onde `won_value`
// guarda o valor da venda que foi desfeita. No estorno parcial esse número não
// está no payload do card, então o chip parcial informa a contagem e não finge
// um valor.
const voidedValueLabel = computed(() => {
  const value = getCardVoidedValue(props.card);
  return value === null ? null : formatCardCurrency(value);
});

const fullyVoidedLabel = computed(() =>
  voidedValueLabel.value
    ? t('PIPELINE.CARD.SALE_VOIDED_WITH_VALUE', {
        value: voidedValueLabel.value,
      })
    : t('PIPELINE.CARD.SALE_VOIDED')
);

const partialVoidLabel = computed(() =>
  t('PIPELINE.CARD.PARTIAL_VOIDED', voidedCount.value, {
    named: { count: voidedCount.value },
  })
);

const moveTargets = computed(() => {
  const stages = store.getters['pipelines/getSelectedPipeline']?.stages || {};
  return Object.entries(stages)
    .map(([id, stage]) => ({ id, ...stage }))
    .filter(stage => String(stage.id) !== String(props.card.pipeline_stage))
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
});

// Get customer name from conversation contact
const customerName = computed(() => {
  // Priority: conversation contact name
  const conversationContact = props.card?.item_details?.conversation?.contact;
  if (conversationContact?.name) return conversationContact.name;
  // Fallback: card contact
  if (props.card?.contact?.name) return props.card.contact.name;
  // Fallback: title
  return displayTitle.value;
});

// Get inbox name from conversation
const inboxName = computed(() => {
  return (
    props.card?.item_details?.conversation?.inbox?.name ||
    props.card?.item_details?.inbox_name ||
    null
  );
});

// Description preview (truncated)
const isAutoDescription = computed(() => {
  const desc = props.card?.item_details?.description || '';
  return /^(Criado automaticamente|Added from conversation|Auto-created)/i.test(
    desc.trim()
  );
});

const descriptionPreview = computed(() => {
  let description = props.card?.item_details?.description;
  if (!description) return null;
  // Clean up #null / #undefined placeholders from older cards
  description = description.replace(/#(null|undefined)/g, '');
  // Hide auto-generated descriptions when conversation link already exists
  if (isAutoDescription.value && hasConversation.value) return null;
  const maxLength = 80;
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength) + '...';
});

// ARIA label for accessibility (Task 2.3)
const cardAriaLabel = computed(() => {
  const parts = [customerName.value];

  if (displayedValue.value) {
    parts.push(displayedValue.value);
  }

  if (hasOpportunityHistory.value) {
    parts.push(
      t('PIPELINE.CARD.OPPORTUNITIES_COUNT', opportunitiesHistoryCount.value)
    );
  }

  if (deadline.value?.text) {
    parts.push(deadline.value.text);
  }

  if (statusLabel.value) {
    parts.push(statusLabel.value);
  }

  return parts.join(', ');
});

// Priority configuration
const priority = computed(() => {
  return getCardPriority(props.card);
});

// Faixa lateral de prioridade. Marca apenas urgente e alta — os níveis que
// pedem atenção na varredura do board. Média e baixa ficam só no ícone, que
// já distingue os quatro níveis pelo número de barras.
// Tokens: só a escala `n-*` resolve (1–12). As classes anteriores
// (`border-l-orange-9`, `border-l-amber-9`, `border-l-blue-9`) apontavam para
// famílias que não existem no tema e nunca pintaram nada.
const priorityBorderClass = computed(() => {
  const classes = {
    urgent: 'border-l-[3px] border-l-n-ruby-9',
    high: 'border-l-[3px] border-l-n-amber-9',
  };
  return classes[priority.value] || '';
});

// Opções de prioridade (dropdown + ícone do gatilho).
// Reusa os ícones canônicos de prioridade do Chatwoot (`i-woot-priority-*`,
// os mesmos do ConversationCard): são auto-coloridos, então dispensam classes
// de cor — que aqui apontavam para tokens inexistentes (`text-orange-11`,
// `bg-blue-3`) e renderizavam cinza. O estado "nenhuma" também deixa de ser
// um círculo tracejado, que era lido como spinner de carregamento.
const priorityOptions = [
  {
    value: 'urgent',
    icon: 'i-woot-priority-urgent',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.URGENT');
    },
  },
  {
    value: 'high',
    icon: 'i-woot-priority-high',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.HIGH');
    },
  },
  {
    value: 'medium',
    icon: 'i-woot-priority-medium',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.MEDIUM');
    },
  },
  {
    value: 'low',
    icon: 'i-woot-priority-low',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.LOW');
    },
  },
  {
    value: 'none',
    icon: 'i-woot-priority-empty',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.NONE');
    },
  },
];

const priorityConfig = computed(() => {
  return (
    priorityOptions.find(o => o.value === priority.value) || priorityOptions[4]
  );
});

// Agent info
const agentInfo = computed(() => {
  // Check owner first (set by backend from owner_id relation)
  if (props.card?.owner) {
    return props.card.owner;
  }
  // Check item_details.agent (set during card creation)
  if (props.card?.item_details?.agent) {
    return props.card.item_details.agent;
  }
  // Fallback to assignee/assigned_agent for older cards
  if (!isAssigned.value) return null;
  return props.card.assignee || props.card.assigned_agent || null;
});

const avatarUrl = computed(() => {
  // Primary: item_details.contact_thumbnail (new flat structure)
  const itemDetails = props.card?.item_details;
  if (itemDetails?.contact_thumbnail && itemDetails.contact_thumbnail.trim()) {
    return itemDetails.contact_thumbnail;
  }
  // Legacy: conversation.contact.thumbnail (old nested structure)
  const conversationContact = itemDetails?.conversation?.contact;
  if (conversationContact?.thumbnail && conversationContact.thumbnail.trim()) {
    return conversationContact.thumbnail;
  }
  if (
    conversationContact?.avatar_url &&
    conversationContact.avatar_url.trim()
  ) {
    return conversationContact.avatar_url;
  }
  // Fallback: card contact thumbnail (from API response)
  if (props.card?.contact?.thumbnail && props.card.contact.thumbnail.trim()) {
    return props.card.contact.thumbnail;
  }
  if (props.card?.contact?.avatar_url && props.card.contact.avatar_url.trim()) {
    return props.card.contact.avatar_url;
  }
  return null;
});

const initials = computed(() => {
  const name = customerName.value || 'N';
  return name.charAt(0).toUpperCase();
});

// Avatar error fallback
const avatarLoadError = ref(false);
const handleAvatarError = () => {
  avatarLoadError.value = true;
};

// Check if title is truncated - measured on hover to avoid layout thrashing.
// O nome quebra em até 2 linhas (line-clamp num elemento -webkit-box), então o
// corte é VERTICAL. O bug histórico era `flex` + `line-clamp-2`: as duas
// utilitárias declaram `display`, o flex vencia e o clamp nunca se aplicava —
// por isso esta medição nunca acusava e o tooltip de nome completo não abria.
const isTitleTruncated = ref(false);
const checkTruncation = () => {
  if (titleElement.value) {
    // Dois eixos: o clamp corta na vertical, mas um token longo sem espaço
    // (número de telefone, URL) é cortado na horizontal dentro da linha sem
    // estourar a altura — e aí o tooltip do nome completo é a única saída.
    const el = titleElement.value;
    isTitleTruncated.value =
      el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth;
  }
};

// Conversation navigation
const conversationUrl = computed(() => {
  if (!hasConversation.value) return null;
  const accountId = props.card?.account_id || store.getters.getCurrentAccountId;
  const conversationId = props.card?.conversation_display_id;
  if (!accountId || !conversationId) return null;
  return `/app/accounts/${accountId}/conversations/${conversationId}`;
});

// Labels da conversa vinculada
const conversationLabels = computed(() => {
  return props.card?.item_details?.conversation?.label_list || [];
});

// Card-4.4 (2026-04-29): suprimir labels que repetem o nome do stage
// atual (ex.: card em "Qualificação" não precisa carregar a tag
// "qualificado" — sobrecarga cognitiva sem ganho informacional).
// Comparação case + whitespace + diacrítico-insensitive.
const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

const filteredConversationLabels = computed(() => {
  const stageKey = normalize(props.stageName);
  if (!stageKey) return conversationLabels.value;
  return conversationLabels.value.filter(
    label => normalize(label) !== stageKey
  );
});

// Card-4.8 (2026-04-29): pedido do cliente — mostrar TODAS as labels,
// quebrando em múltiplas linhas quando não couberem. Antes (Card-4.3)
// limitava a 3 visíveis + "+N" pill para preservar altura constante;
// trade-off agora favorece visibilidade total das tags (UX > altura
// previsível, já que cards já têm altura variável por outras razões).
const visibleLabels = computed(() => filteredConversationLabels.value);

// Round-3 (2026-04-25): hover em QUALQUER label (visível ou no "+N")
// mostra a lista completa de etiquetas vinculadas. Antes só o "+N"
// expunha as ocultas — o que cortava o contexto ao agente que queria
// ver tudo de uma vez sem clicar pra abrir o card.
const allLabelsText = computed(() =>
  filteredConversationLabels.value.length
    ? filteredConversationLabels.value.join(', ')
    : ''
);

// Obtém a cor da label do mapa ou retorna cor padrão
const getLabelColor = label => {
  const mapValue = labelsMap?.value || labelsMap;
  return mapValue?.[label] || null;
};
const getLabelBadgeClass = label =>
  getStageColorClasses(getLabelColor(label)).badge;
// Label também tem cor livre: fora das 5 conhecidas o selo ficava sem cor.
const getLabelBadgeStyle = label =>
  stageColorStyle(getLabelColor(label), 'badge');

// ── Collapse / expand ──────────────────────────────────────────────────────
// UX Card-Q1.2 (2026-04-22): inject estado centralizado do PipelineView
// (lê localStorage 1× por board mount, não 3× por card como antes).
// Antes: cada card lia + parseava localStorage 2× no mount e 1× a cada
// toggle. Com 100 cards = 200+ reads sincronas no boot.
const collapsedItems = inject('collapsedItems', ref({}));
const updateCollapsedItem = inject('updateCollapsedItem', () => {});

const isCollapsed = ref(false);

// Watcher reativo: aplica do parent injetado.
watch(
  () => collapsedItems.value[props.card.id],
  newCollapsed => {
    if (newCollapsed !== undefined) isCollapsed.value = newCollapsed;
  },
  { immediate: true }
);

const toggleCollapse = event => {
  event.stopPropagation();
  isCollapsed.value = !isCollapsed.value;
  updateCollapsedItem(props.card.id, isCollapsed.value);
};

// 5.4: surface the next pending checklist item directly on the kanban card.
// The board index ships a lightweight `next_checklist_item` ({ text }) — the
// full checklist array is not on board cards. Fall back to scanning the full
// checklist when present (e.g. a card hydrated with item_details.checklist).
const nextChecklistItem = computed(() => {
  const projected = props.card?.next_checklist_item;
  if (projected?.text) return projected;
  const checklist = props.card?.item_details?.checklist;
  if (!Array.isArray(checklist)) return null;
  return checklist.find(i => i && !i.completed) || null;
});

const handleOpenDetails = event => {
  if (event?.altKey) {
    toggleCollapse(event);
    return;
  }
  emit('openDetails', props.card);
};

const handleDragStart = event => {
  // `canDrag` e não `props.draggable`: o atributo já impede o arrasto com o
  // histórico aberto, e esta guarda cobre o caso de o navegador já ter iniciado
  // o gesto quando o modal abriu.
  if (!canDrag.value) {
    event.preventDefault();
    return;
  }

  // Stop propagation to prevent column drag from interfering
  event.stopPropagation();

  isDragging.value = true;
  event.dataTransfer.setData('text/plain', props.card.id.toString());
  event.dataTransfer.effectAllowed = 'move';

  // Set drag image to the card element for better visual feedback
  const cardElement = event.currentTarget;
  if (cardElement) {
    event.dataTransfer.setDragImage(cardElement, 20, 20);
  }

  emit('dragstart', event);
};

const handleDragEnd = () => {
  isDragging.value = false;
  emit('dragend');
};

const closeStatusMenu = () => {
  showStatusMenu.value = false;
  statusPopoverRef.value?.hide();
};

const openStatusMenu = (focusPosition = 'first') => {
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  showStatusMenu.value = true;
  statusPopoverRef.value?.show();
  nextTick(() => focusMenuItem(statusDropdownRef, focusPosition));
};

const toggleStatusMenu = () => {
  if (showStatusMenu.value) {
    closeStatusMenu();
    return;
  }
  openStatusMenu();
};

// Só o badge INTERATIVO engole o clique. Quando won/lost estão desabilitados,
// o status é um <div> inerte: parar a propagação ali criaria uma zona morta no
// rodapé, onde o clique não abriria nem o menu nem os detalhes do card.
const handleStatusClick = event => {
  if (!canChangeStatus.value) return;
  event.stopPropagation();
  toggleStatusMenu();
};

const changeStatus = newStatus => {
  closeStatusMenu();
  nextTick(() => focusElement(statusButtonRef));
  if (newStatus !== cardStatus.value) {
    emit('statusChange', { card: props.card, status: newStatus });
  }
};

// Open conversation in inbox.
// A propagação é parada no template (`@click.stop`), onde o risco é visível:
// o <article> inteiro é clicável, então um clique aqui abriria a conversa E os
// detalhes do card.
const openConversation = () => {
  if (conversationUrl.value) {
    router.push(conversationUrl.value);
  }
};

// Actions menu handlers
const openActionsMenu = (focusPosition = 'first') => {
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  showActionsMenu.value = true;
  actionsPopoverRef.value?.show();
  nextTick(() => {
    focusMenuItem(actionsDropdownRef, focusPosition);
  });
};

const closeActionsMenu = () => {
  showActionsMenu.value = false;
  actionsPopoverRef.value?.hide();
};

const toggleActionsMenu = event => {
  event.stopPropagation();
  if (showActionsMenu.value) {
    closeActionsMenu();
    return;
  }
  openActionsMenu();
};

// Recolher/expandir a partir do menu de ações: fecha o menu e devolve o foco
// ao gatilho, como os demais itens do menu.
const handleToggleCollapse = event => {
  toggleCollapse(event);
  closeActionsMenu();
  nextTick(() => focusElement(actionsButtonRef));
};

const handleSelectPriority = async newPriority => {
  if (newPriority === priority.value || isUpdatingPriority.value) return;
  isUpdatingPriority.value = true;
  try {
    const { data } = await PipelineCardsAPI.bulkSetPriority({
      cardIds: [props.card.id],
      priority: newPriority,
    });
    const hasExactUpdatedIds = Array.isArray(data?.updated_ids);
    const priorityWasUpdated = hasExactUpdatedIds
      ? data.updated_ids.some(id => String(id) === String(props.card.id))
      : Number(data?.updated) === 1;
    if (!priorityWasUpdated) {
      showAlert(t('PIPELINE.CARD.PRIORITY_UPDATE_ERROR'));
      return;
    }
    store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', {
      id: props.card.id,
      priority: newPriority,
      item_details: {
        ...props.card.item_details,
        priority: newPriority,
      },
    });
    closeActionsMenu();
    nextTick(() => focusElement(actionsButtonRef));
  } catch {
    showAlert(t('PIPELINE.CARD.PRIORITY_UPDATE_ERROR'));
  } finally {
    isUpdatingPriority.value = false;
  }
};

// Agent dropdown handlers — UX 2026-04-22
const availableAgents = computed(() => {
  return store.getters['agents/getVerifiedAgents'] || [];
});

const filteredAgents = computed(() => {
  const q = agentSearchQuery.value.trim().toLowerCase();
  if (!q) return availableAgents.value;
  return availableAgents.value.filter(a =>
    (a.name || a.email || '').toLowerCase().includes(q)
  );
});

const closeAgentDropdown = () => {
  showAgentDropdown.value = false;
  agentPopoverRef.value?.hide();
};

// Auditoria 2026-08-08 (F24): o dropdown fechava antes da resposta e nada
// indicava que a atribuição estava em curso — dois cliques seguidos disparavam
// duas requisições, e o usuário não sabia se tinha funcionado.
const isAssigningAgent = ref(false);

const openAgentDropdown = () => {
  // `aria-disabled` (não `disabled`) mantém o botão focável durante a
  // atribuição — `disabled` tiraria o foco do teclado a cada atribuição. Mas
  // aria-disabled não bloqueia evento nenhum: o guard tem que estar AQUI,
  // porque as setas do teclado chamam esta função direto (cross-review Codex).
  if (isAssigningAgent.value) return;
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  agentSearchQuery.value = '';
  showAgentDropdown.value = true;
  agentPopoverRef.value?.show();
  nextTick(() => {
    focusElement(agentSearchRef);
  });
};

const toggleAgentDropdown = event => {
  event.stopPropagation();
  // Enquanto a atribuição está em curso, o menu não reabre — evita escolher um
  // segundo agente cuja seleção seria descartada.
  if (isAssigningAgent.value) return;
  if (showAgentDropdown.value) {
    closeAgentDropdown();
    return;
  }
  openAgentDropdown();
};

const closeAgentDropdownAndRestoreFocus = () => {
  closeAgentDropdown();
  nextTick(() => focusElement(agentButtonRef));
};

const getAgentOptions = () => {
  if (!agentListRef.value) return [];
  return [
    ...agentListRef.value.querySelectorAll('[role="option"]:not([disabled])'),
  ];
};

const focusAgentOption = position => {
  const options = getAgentOptions();
  if (!options.length) return;
  const target = position === 'last' ? options.at(-1) : options[0];
  target?.focus();
};

const handleAgentListKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeAgentDropdownAndRestoreFocus();
    return;
  }

  const options = getAgentOptions();
  if (!options.length) return;
  const currentIndex = options.indexOf(document.activeElement);
  let targetIndex = null;
  if (event.key === 'ArrowDown') {
    targetIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
  }
  if (event.key === 'ArrowUp') {
    targetIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
  }
  if (event.key === 'Home') targetIndex = 0;
  if (event.key === 'End') targetIndex = options.length - 1;

  if (targetIndex !== null) {
    event.preventDefault();
    event.stopPropagation();
    options[targetIndex]?.focus();
  }
};

const handleSelectAgent = async agentId => {
  // Cross-review Codex: o guard vem ANTES de fechar. Fechando primeiro, uma
  // segunda escolha durante a requisição era descartada sem o usuário perceber.
  if (isAssigningAgent.value) return;
  closeAgentDropdownAndRestoreFocus();
  // Se clicou no mesmo agente já atribuído, no-op
  if (agentInfo.value && agentInfo.value.id === agentId) return;
  isAssigningAgent.value = true;
  try {
    await store.dispatch('pipelineCards/updateItemOwner', {
      itemId: props.card.id,
      ownerId: agentId,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCard] handleSelectAgent error:', e);
    showAlert(t('PIPELINE.MESSAGES.AGENT_ASSIGN_ERROR'));
  } finally {
    isAssigningAgent.value = false;
  }
};

const handleUnassignAgent = async () => {
  if (isAssigningAgent.value) return;
  closeAgentDropdownAndRestoreFocus();
  if (!agentInfo.value) return;
  isAssigningAgent.value = true;
  try {
    await store.dispatch('pipelineCards/updateItemOwner', {
      itemId: props.card.id,
      ownerId: null,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCard] handleUnassignAgent error:', e);
    showAlert(t('PIPELINE.MESSAGES.AGENT_ASSIGN_ERROR'));
  } finally {
    isAssigningAgent.value = false;
  }
};

// Close all dropdowns in this card (used by both card-level and pipeline-level events)
const closeAllCardDropdowns = () => {
  closeActionsMenu();
  closeStatusMenu();
  closeAgentDropdown();
};

// Close dropdowns when clicking outside or when another card opens its dropdown
const handleCloseAllDropdowns = excludeCardId => {
  if (excludeCardId !== props.card.id) {
    closeAllCardDropdowns();
  }
};

// Close on global pipeline event (column menu, header dropdowns, etc.)
const handleClosePipelineDropdowns = () => {
  closeAllCardDropdowns();
};

// Close on click outside
const handleDocumentClick = () => {
  closeAllCardDropdowns();
};

// Card-2B (2026-04-22): vOnClickOutside no template root substitui
// document.addEventListener. Antes 100 cards = 100 listeners no document.
// emitter de closeAllCardDropdowns/closeAllPipelineDropdowns mantido
// — usado para sincronizar fechamento entre cards do mesmo board.
onMounted(() => {
  emitter.on('closeAllCardDropdowns', handleCloseAllDropdowns);
  emitter.on('closeAllPipelineDropdowns', handleClosePipelineDropdowns);
  // Phase 1.2: Lazy-fetch active sequence for badge. No-op if already cached
  // or another fetch is in-flight (guards inside the action).
  // Audit 2026-05-07 — só dispara fetch se:
  //   1. feature pipeline_sequences ligada (zero 404s quando off)
  //   2. card.account_id bate com a route accountId atual (zero requests
  //      cross-account durante transição de rota — Vai Pri / ClickHype bug).
  // A conta vem da URL, não do store: `getCurrentAccountId` pode estar nulo
  // durante a hidratação e na troca de conta, e a guarda abaixo é fail-open
  // (nulo conta como "bate"). Ler a rota mantém a checagem síncrona e correta.
  // Endurecer a guarda para fail-closed é mudança à parte, fora deste ciclo.
  const routeAcct = (() => {
    const seg = window.location.pathname.split('/');
    const idx = seg.indexOf('accounts');
    return idx === -1 ? null : Number(seg[idx + 1]);
  })();
  const cardAcct = props.card.account_id ? Number(props.card.account_id) : null;
  const cardAccountMatches =
    cardAcct == null || routeAcct == null || cardAcct === routeAcct;
  if (isSequencesEnabled.value && cardAccountMatches) {
    store.dispatch('pipelineCardSequences/fetchForCard', props.card.id);
  }
});

onUnmounted(() => {
  emitter.off('closeAllCardDropdowns', handleCloseAllDropdowns);
  emitter.off('closeAllPipelineDropdowns', handleClosePipelineDropdowns);
});

const handleEdit = event => {
  event.stopPropagation();
  closeActionsMenu();
  emit('edit', props.card);
};

// Card-4.6 (2026-04-29) — click no badge "Prazo" abre o form de
// edição (PipelineCardForm) focado em deadline/forecast_close_date.
// Prazo é propriedade interna do card.
const handleEditDeadline = event => {
  event.stopPropagation();
  emit('edit', props.card);
};

// Card-4.7 (2026-04-29) — click no badge "Agendado" navega para a
// view Agenda do pipeline, onde o agente vê todos os agendamentos em
// calendário. scheduled_at é semanticamente um compromisso (sync com
// Google Calendar via google_event_id), não uma propriedade interna.
//
// Comportamento alternativo (futuro): ?highlight=cardId no query string
// para destacar o item específico no calendário.
const handleOpenAgenda = event => {
  event.stopPropagation();
  const route = router.currentRoute.value;
  // Substitui /board ou /list por /agenda mantendo accountId e pipelineSlug
  const newPath = route.fullPath
    .replace(/\/board(\?|$)/, '/agenda$1')
    .replace(/\/list(\?|$)/, '/agenda$1')
    .replace(/\/dashboard(\?|$)/, '/agenda$1');
  if (newPath !== route.fullPath) {
    router.push(
      newPath + (newPath.includes('?') ? '&' : '?') + `card=${props.card.id}`
    );
  } else {
    // Fallback: emit edit (caso a URL não seja convertível)
    emit('edit', props.card);
  }
};

// ── Sequence badge ────────────────────────────────────────────────────────
// Phase 1.2: Reads cached active sequence progress from the Vuex store.
// Data is populated reactively when CardSequencesSection fetches sequences
// (on card detail open). On cold-start (card never opened), falls back to
// an on-demand fetch so the badge eventually shows without blocking render.
const activeSequenceProgress = computed(() =>
  store.getters['pipelineCardSequences/getActiveSequenceForCard'](props.card.id)
);

const progressPercent = computed(() => {
  const seq = activeSequenceProgress.value;
  if (!seq || !seq.total_steps) return 0;
  return Math.round((seq.current_step / seq.total_steps) * 100);
});

const sequenceTooltip = computed(() => {
  const seq = activeSequenceProgress.value;
  if (!seq) return '';
  return t('PIPELINE.SEQUENCES.BADGE_TOOLTIP', {
    name: seq.name || t('PIPELINE.SEQUENCES.UNNAMED'),
    current: seq.current_step,
    total: seq.total_steps,
  });
});

// Rodapé cheio = sequência ativa OU conversa vinculada. Nesses casos o nome do
// agente encolhe até virar uma letra ao lado de um avatar que já mostra a MESMA
// letra ("A A…"), o que não informa nada. Fica só o avatar, com tooltip.
const isFooterCrowded = computed(
  () => Boolean(activeSequenceProgress.value) || hasConversation.value
);

// Auditoria 2026-08-08 (F24): emitia também `openSequences`, que ninguém
// escutava. Não havia o que escutar: a seção de sequências vive no painel do
// próprio detalhe (CardAssigneePanel), então abrir o card já leva até ela.
const handleOpenSequences = event => {
  event.stopPropagation();
  emit('openDetails', props.card);
};

const handleDelete = event => {
  event.stopPropagation();
  if (!canDeleteCard.value) return;
  closeActionsMenu();
  emit('delete', props.card);
};

const handleMove = (event, columnId) => {
  event.stopPropagation();
  closeActionsMenu();
  emit('move', { itemId: props.card.id, columnId });
};

const handleViewDetails = event => {
  event.stopPropagation();
  closeActionsMenu();
  emit('openDetails', props.card);
};
</script>

<template>
  <article
    v-on-click-outside="[
      handleDocumentClick,
      {
        ignore: [
          `#card-actions-menu-${card.id}`,
          `#card-status-menu-${card.id}`,
          `#card-agent-menu-${card.id}`,
        ],
      },
    ]"
    class="pipeline-card group relative select-none overflow-visible rounded-xl border border-n-weak bg-n-solid-1 p-3 shadow-sm transition-shadow duration-200 cursor-pointer hover:shadow-md focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:outline-offset-2 motion-reduce:transition-none"
    :class="[
      priorityBorderClass,
      {
        'cursor-grab': canDrag && !isDragging,
        'cursor-grabbing opacity-50 scale-105 rotate-2 shadow-2xl ring-4 ring-n-brand z-50':
          isDragging,
      },
    ]"
    :draggable="canDrag"
    role="listitem"
    :aria-label="cardAriaLabel"
    :aria-describedby="`card-details-${card.id}`"
    @click="handleOpenDetails"
    @keydown.esc.stop="closeAllCardDropdowns"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Card-4.2 (2026-04-22): drag handle absolute REMOVIDO.
         O `cursor-grab` no card + `border-l-3` da priority já dão
         affordance suficiente — o handle absolute competia com avatar
         e nome em hover. Drag and drop continua funcional via HTML5
         draggable attribute. -->

    <!-- ── Faixa 1: identidade ───────────────────────────────────────────
         Redesenho 2026-08-05. O nome volta para a MESMA linha do avatar.
         O layout anterior (Card-4.5) separava avatar na linha 1 e nome na
         linha 3, e os três botões de 44×44 consumiam 148 dos 208px úteis:
         sobravam 20px para o rótulo do inbox, que nunca era legível em
         nenhum card do board (medido em chatdev).
         Controles agora são 28×28 com área de toque estendida por
         pseudo-elemento (`after:absolute after:-inset-*`), que amplia o alvo
         sem inflar a caixa visual: 44×44 no botão de ações, 36px de altura nos
         badges inline do rodapé — ali 44px sobreporia a linha vizinha, e
         sobrepor controles adjacentes é pior que ficar abaixo do mínimo. -->
    <div>
      <div class="flex items-center gap-2">
        <!-- Avatar -->
        <div
          v-if="avatarUrl && !avatarLoadError"
          class="size-6 rounded-full overflow-hidden flex-shrink-0"
        >
          <img
            :src="avatarUrl"
            :alt="customerName"
            class="w-full h-full object-cover"
            @error="handleAvatarError"
          />
        </div>
        <div
          v-else
          class="size-6 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0"
        >
          <span class="text-xxs font-bold text-n-brand">
            {{ initials }}
          </span>
        </div>

        <!-- Prioridade: indicador, não controle. A troca acontece no menu de
             ações. Um segundo botão nesta linha custava ~30px permanentes de
             largura (opacity-0 não devolve a caixa) e truncava o nome do
             cliente em 242px de coluna. Só aparece quando há prioridade —
             "nenhuma" não merece espaço no card. -->
        <span
          v-if="priority !== 'none'"
          v-tooltip="{
            content: priorityConfig.label,
            delay: { show: 400, hide: 0 },
            placement: 'top',
          }"
          class="size-4 flex-shrink-0"
          :class="priorityConfig.icon"
          role="img"
          :aria-label="priorityConfig.label"
        />

        <!-- Nome do cliente -->
        <h3 class="min-w-0 flex-1 text-heading-3 font-semibold text-n-slate-12">
          <button
            ref="titleElement"
            v-tooltip="{
              content: isTitleTruncated ? customerName : null,
              delay: { show: 500, hide: 0 },
              placement: 'top',
            }"
            type="button"
            class="w-full rounded text-left line-clamp-2 [display:-webkit-box] focus-visible:outline-2 focus-visible:outline-n-brand"
            @click.stop="handleOpenDetails"
            @mouseenter="checkTruncation"
          >
            {{ customerName }}
          </button>
        </h3>

        <!-- Único controle da linha. Prioridade e recolher/expandir viraram
             itens do menu: os três botões de 44px do layout anterior não
             cabiam em 242px de coluna, e recolher ainda usava as strings
             COLUMN_OPTIONS.*_ALL ("Recolher todos"), que descrevem a ação da
             COLUNA e não a deste card. Alt+clique no card continua
             alternando o recolhimento direto. -->
        <div class="flex-shrink-0 flex items-center gap-0.5">
          <!-- Menu de ações do card -->
          <Popover
            ref="actionsPopoverRef"
            align="end"
            disable-mobile-view
            :show-content-border="false"
            @hide="showActionsMenu = false"
          >
            <button
              :id="`card-actions-trigger-${card.id}`"
              ref="actionsButtonRef"
              v-tooltip="{
                content: t('PIPELINE.CARD.ACTIONS'),
                delay: { show: 200, hide: 0 },
                placement: 'top',
              }"
              type="button"
              :aria-label="t('PIPELINE.CARD.ACTIONS')"
              :aria-expanded="showActionsMenu"
              :aria-controls="`card-actions-menu-${card.id}`"
              aria-haspopup="menu"
              class="relative size-8 rounded-full text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-[color,background-color,opacity] opacity-60 group-hover:opacity-100 focus-visible:opacity-100 flex items-center justify-center after:absolute after:-inset-1.5 focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="{
                'opacity-100 bg-n-alpha-2': showActionsMenu,
              }"
              @click="toggleActionsMenu"
              @keydown.down.prevent.stop="openActionsMenu('first')"
              @keydown.up.prevent.stop="openActionsMenu('last')"
            >
              <FluentIcon icon="more-vertical" size="16" />
            </button>

            <!-- Actions Dropdown -->
            <!-- `@mousedown.prevent`: o menu é ROLÁVEL (max-h-80 + overflow) e
                 tem `@focusout`. O mousedown nativo tira o foco do item, o
                 focusout dispara e o container volta a scrollTop 0 ANTES do
                 mouseup. Com o conteúdo deslizando sob o cursor, mousedown e
                 mouseup caem em elementos diferentes e o browser emite o
                 `click` no ancestral comum (o próprio menu) — nunca no botão.
                 Era isso que deixava "Excluir" inerte para quem rolava até ele
                 (relato de cliente 2026-08-11); os itens do topo funcionavam
                 porque não exigiam rolagem. Prevenir o default do mousedown é
                 o padrão de menu: mantém o foco, mantém o scroll, e o clique
                 chega ao item. Teclado segue por @keydown, sem mudança. -->
            <template #content>
              <div
                v-if="showActionsMenu"
                :id="`card-actions-menu-${card.id}`"
                ref="actionsDropdownRef"
                role="menu"
                :aria-labelledby="`card-actions-trigger-${card.id}`"
                class="w-60 max-h-80 overflow-y-auto bg-n-solid-3 rounded-xl shadow-2xl outline outline-1 outline-n-weak py-1 origin-top-right"
                @click.stop
                @mousedown.prevent
                @keydown="
                  event =>
                    handleMenuKeydown(event, actionsButtonRef, closeActionsMenu)
                "
                @focusout="
                  event => closeAfterFocusLeaves(event, closeActionsMenu)
                "
              >
                <button
                  type="button"
                  role="menuitem"
                  tabindex="-1"
                  class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="handleViewDetails"
                >
                  <FluentIcon
                    icon="eye-show"
                    size="14"
                    class="text-n-slate-9"
                  />
                  {{ t('PIPELINE.CARD.VIEW_DETAILS') }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  tabindex="-1"
                  class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="handleEdit"
                >
                  <FluentIcon icon="edit" size="14" class="text-n-slate-9" />
                  {{ t('PIPELINE.CARD.EDIT') }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  tabindex="-1"
                  class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="handleToggleCollapse"
                >
                  <FluentIcon
                    :icon="isCollapsed ? 'arrow-expand' : 'arrow-outwards'"
                    size="14"
                    class="text-n-slate-9"
                  />
                  {{
                    isCollapsed
                      ? t('PIPELINE.CARD.EXPAND')
                      : t('PIPELINE.CARD.COLLAPSE')
                  }}
                </button>

                <!-- Prioridade — `menuitemradio`, já suportado pela navegação
                     por teclado do menu (getMenuItems seleciona ambos os
                     papéis). Deixou de ser um botão próprio no topo do card. -->
                <div class="h-px bg-n-weak my-1" />
                <div
                  :id="`card-priority-menu-${card.id}`"
                  ref="priorityDropdownRef"
                  role="group"
                  :aria-busy="isUpdatingPriority"
                  :aria-label="t('PIPELINE.CARD.PRIORITY.LABEL')"
                >
                  <button
                    v-for="option in priorityOptions"
                    :key="option.value"
                    type="button"
                    role="menuitemradio"
                    tabindex="-1"
                    :aria-checked="priority === option.value"
                    :disabled="isUpdatingPriority"
                    class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                    :class="{
                      'bg-n-alpha-1 font-semibold': priority === option.value,
                    }"
                    @click="handleSelectPriority(option.value)"
                  >
                    <span class="size-4 flex-shrink-0" :class="option.icon" />
                    {{ option.label }}
                  </button>
                </div>

                <template v-if="moveTargets.length">
                  <div class="h-px bg-n-weak my-1" />
                  <button
                    v-for="stage in moveTargets"
                    :key="stage.id"
                    type="button"
                    role="menuitem"
                    tabindex="-1"
                    class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                    @click="event => handleMove(event, stage.id)"
                  >
                    <span
                      data-test="stage-color-dot"
                      class="size-2.5 shrink-0 rounded-full"
                      :class="getStageColorClasses(stage.color).dot"
                      :style="stageColorStyle(stage.color, 'dot')"
                    />
                    <span class="truncate">{{
                      t('PIPELINE.STEPPER.MOVE_TO', {
                        stage: stage.name || stage.id,
                      })
                    }}</span>
                  </button>
                </template>
                <div v-if="canDeleteCard" class="h-px bg-n-weak my-1" />
                <button
                  v-if="canDeleteCard"
                  type="button"
                  role="menuitem"
                  tabindex="-1"
                  class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-ruby-3 flex items-center gap-2 text-n-ruby-11 transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="handleDelete"
                >
                  <FluentIcon icon="delete" size="14" />
                  {{ t('PIPELINE.CARD.DELETE') }}
                </button>
              </div>
            </template>
          </Popover>
        </div>
      </div>
      <!-- /faixa 1 -->

      <!-- ── Faixa 2: contexto ────────────────────────────────────────────
           Inbox e idade do card em uma linha só, com o inbox truncando
           primeiro. Continua visível com o card recolhido — é o par de
           dados que responde "de onde veio e há quanto tempo está aqui".
           A idade usa `cardLifetimeShort` (sem a hora de criação): o texto
           anterior, "Há 2 meses 21:16", juntava duas unidades sem rótulo e
           reusava o ícone de calendário do badge "Agendado". -->
      <p
        v-if="inboxName || cardLifetimeShort"
        class="mt-2 flex items-center gap-1.5 text-label-small text-n-slate-10"
      >
        <template v-if="inboxName">
          <FluentIcon
            icon="mail-inbox"
            size="11"
            class="opacity-70 flex-shrink-0"
          />
          <span class="truncate">{{ inboxName }}</span>
        </template>
        <span
          v-if="inboxName && cardLifetimeShort"
          aria-hidden="true"
          class="flex-shrink-0 text-n-slate-8 before:content-['·']"
        />
        <span v-if="cardLifetimeShort" class="flex-shrink-0">
          {{ cardLifetimeShort }}
        </span>
      </p>
    </div>

    <!-- Card body — hidden when collapsed -->
    <div v-if="!isCollapsed">
      <!-- Description Preview -->
      <p
        v-if="descriptionPreview"
        v-tooltip="{
          content: props.card?.item_details?.description,
          delay: { show: 300, hide: 0 },
          placement: 'top',
        }"
        class="text-label-small text-n-slate-11 mt-2 line-clamp-2"
      >
        {{ descriptionPreview }}
      </p>

      <!-- 5.4: próxima ação pendente do checklist -->
      <div
        v-if="nextChecklistItem"
        v-tooltip="{
          content: nextChecklistItem.text,
          delay: { show: 300, hide: 0 },
          placement: 'top',
        }"
        class="flex items-center gap-1.5 mt-2 text-label-small text-n-slate-11"
      >
        <FluentIcon
          icon="list"
          size="12"
          class="flex-shrink-0 text-n-slate-10"
        />
        <span class="truncate">{{ nextChecklistItem.text }}</span>
      </div>

      <!-- ── Faixa 3: valor e datas ───────────────────────────────────────
           O valor é o único elemento em 16px do card — é o dado comercial e
           precisa vencer a varredura. Antes dividia a mesma pílula cinza de
           12px com o status. Prazo e agendamento perderam o prefixo textual
           ("Prazo:", "Agendado:"): o ícone, a cor e o tooltip já carregam o
           significado, e sem o prefixo os dois cabem na mesma linha. -->
      <div
        v-if="
          displayedValue ||
          hasOpportunityHistory ||
          deadline?.text ||
          hasScheduledDate ||
          isSlaExceeded
        "
        class="mt-2.5 flex flex-wrap items-center gap-2"
      >
        <!-- Fase 59: verde = receita REALIZADA (soma das oportunidades já
             ganhas); cor neutra = valor PREVISTO de um card que ainda não
             vendeu. A distinção é a razão de existir da feature — misturar os
             dois num número só inflaria a previsão com dinheiro já entrado. -->
        <!-- Quando o número é receita realizada, ele próprio abre a lista: é
             onde a pessoa clica para perguntar "de onde veio esse número?".
             Previsto continua sendo texto — botão que não faz nada é pior. -->
        <button
          v-if="showsRealizedValue"
          v-tooltip="{
            content: t('PIPELINE.CARD.OPPORTUNITIES_VALUE_TOOLTIP'),
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          class="rounded text-heading-2 font-semibold tabular-nums text-n-teal-11 underline decoration-dashed decoration-from-font underline-offset-4 transition-colors hover:text-n-teal-10 focus-visible:outline-2 focus-visible:outline-n-brand"
          @click.stop="showOpportunityHistory = true"
        >
          {{ displayedValue }}
        </button>
        <span
          v-else-if="displayedValue"
          class="text-heading-2 font-semibold tabular-nums text-n-slate-12"
        >
          {{ displayedValue }}
        </span>
        <!-- Estorno TOTAL: o card diz que a venda foi cancelada E de quanto
             era. Em ruby, que é o tom de "isto saiu da receita" — o mesmo do
             histórico. Sem este chip, um card com tudo estornado exibia um
             número em cor neutra, indistinguível de um card que nunca vendeu. -->
        <span
          v-if="isFullyVoided"
          class="inline-flex items-center gap-1 rounded-md bg-n-ruby-3 px-1.5 py-0.5 text-label-small font-medium text-n-ruby-11"
          data-testid="card-void-full"
        >
          <span class="i-lucide-rotate-ccw size-3.5" aria-hidden="true" />
          {{ fullyVoidedLabel }}
        </span>
        <!-- Estorno PARCIAL: tom mais quieto e SEM valor, porque o card continua
             com receita realizada (o número verde ao lado é o que sobrou) e o
             valor da linha estornada não viaja no payload do card. Um chip igual
             ao de cima faria "uma de três estornada" parecer "venda cancelada",
             que é justamente a confusão que este sinal existe para desfazer. -->
        <span
          v-else-if="hasPartialVoid"
          class="inline-flex items-center gap-1 rounded-md bg-n-amber-3 px-1.5 py-0.5 text-label-small font-medium text-n-amber-11"
          data-testid="card-void-partial"
        >
          <span class="i-lucide-rotate-ccw size-3.5" aria-hidden="true" />
          {{ partialVoidLabel }}
        </span>
        <button
          v-if="hasOpportunityHistory"
          v-tooltip="{
            content: opportunitiesChipLabel,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          :aria-label="opportunitiesChipLabel"
          class="relative rounded-full border border-dashed border-n-teal-9 bg-n-teal-3 px-2 py-0.5 text-label-small font-semibold text-n-teal-11 transition-colors after:absolute after:-inset-y-2 after:inset-x-0 hover:bg-n-teal-4 focus-visible:outline-2 focus-visible:outline-n-brand"
          @click.stop="showOpportunityHistory = true"
        >
          {{
            t('PIPELINE.CARD.OPPORTUNITIES_COUNT', opportunitiesHistoryCount)
          }}
        </button>
        <!-- SLA da etapa estourado. Não é clicável: o limite é propriedade da
             ETAPA (editada no funil), não do card — botão que não leva a lugar
             nenhum seria pior. Fica fora do grupo `ml-auto` para participar do
             flex-wrap da faixa em vez de espremer os badges de data. -->
        <span
          v-if="isSlaExceeded"
          v-tooltip="{
            content: slaTooltip,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          :aria-label="slaTooltip"
          class="flex cursor-help items-center gap-1 rounded-md bg-n-ruby-3 px-1.5 py-0.5 text-label-small font-medium text-n-ruby-11"
        >
          <FluentIcon icon="alarm" size="12" />
          <span>{{ t('PIPELINE.CARD.SLA_EXCEEDED') }}</span>
        </span>
        <div class="ml-auto flex items-center gap-1.5">
          <!-- Agendado — click navega para a view Agenda (compromisso
               sincronizado com o Google Calendar), não edita inline. -->
          <button
            v-if="hasScheduledDate"
            v-tooltip="{
              content: scheduledAt?.fullDateTime
                ? `${t('PIPELINE.CARD.SCHEDULED_AT')} — ${scheduledAt.fullDateTime}`
                : t('PIPELINE.CARD.SCHEDULED_AT'),
              delay: { show: 200, hide: 0 },
              placement: 'top',
            }"
            type="button"
            :aria-label="`${t('PIPELINE.CARD.SCHEDULED_AT')}: ${scheduledAt.text}`"
            class="relative flex items-center gap-1 rounded-md bg-n-violet-3 px-1.5 py-0.5 text-label-small font-medium text-n-violet-11 transition-colors after:absolute after:-inset-y-2 after:inset-x-0 hover:bg-n-violet-4 focus-visible:outline-2 focus-visible:outline-n-brand"
            @click.stop="handleOpenAgenda"
          >
            <FluentIcon icon="calendar-clock" size="12" />
            <span>{{ scheduledAt.text }}</span>
          </button>
          <!-- Prazo — propriedade interna do card; click abre a edição. -->
          <button
            v-if="deadline?.text"
            v-tooltip="{
              content: deadline.fullDateTime
                ? `${t('PIPELINE.CARD.DEADLINE')} — ${deadline.fullDateTime}`
                : t('PIPELINE.CARD.DEADLINE'),
              delay: { show: 200, hide: 0 },
              placement: 'top',
            }"
            type="button"
            :aria-label="`${t('PIPELINE.CARD.DEADLINE')}: ${deadline.text}`"
            class="relative flex items-center gap-1 rounded-md px-1.5 py-0.5 text-label-small font-medium transition-opacity after:absolute after:-inset-y-2 after:inset-x-0 hover:opacity-80 focus-visible:outline-2 focus-visible:outline-n-brand"
            :class="deadlineBadgeClasses"
            @click.stop="handleEditDeadline"
          >
            <FluentIcon v-if="isOverdue" icon="warning" size="12" />
            <FluentIcon v-else icon="alarm" size="12" />
            <span>{{ deadline.text }}</span>
          </button>
        </div>
      </div>

      <!-- ── Faixa 4: etiquetas ───────────────────────────────────────────
           Card-4.8 (2026-04-29): mostra TODAS as etiquetas, quebrando em
           múltiplas linhas. Pedido explícito do cliente — prefere ver tudo a
           ter altura previsível. Mantido. -->
      <div
        v-if="filteredConversationLabels.length > 0"
        v-tooltip="allLabelsText"
        :aria-label="allLabelsText"
        class="flex flex-wrap items-center gap-1 mt-2 cursor-help"
      >
        <span
          v-for="label in visibleLabels"
          :key="label"
          class="inline-flex items-center px-2 py-0.5 rounded text-label-small flex-shrink-0 truncate max-w-[110px]"
          :class="getLabelBadgeClass(label)"
          :style="getLabelBadgeStyle(label)"
        >
          {{ label }}
        </span>
      </div>

      <!-- ── Faixa 5: rodapé ──────────────────────────────────────────────
           Status | agente (elástico) | sequência | conversa. O status desce
           do meio do card para cá: com o valor promovido, a linha do meio
           passa a ser só dinheiro e datas. -->
      <div
        class="flex items-center gap-2 mt-2.5 pt-2 border-t border-n-weak/60"
      >
        <!-- Status. Passou a usar Popover como os outros três menus do card:
             o dropdown anterior era `absolute … z-20` dentro do card, o único
             sem teleport, sujeito a recorte pelo scroll da coluna. -->
        <div class="flex-shrink-0">
          <Popover
            ref="statusPopoverRef"
            align="start"
            disable-mobile-view
            :show-content-border="false"
            @hide="showStatusMenu = false"
          >
            <component
              :is="canChangeStatus ? 'button' : 'div'"
              :id="
                canChangeStatus ? `card-status-trigger-${card.id}` : undefined
              "
              ref="statusButtonRef"
              data-test="card-status"
              :type="canChangeStatus ? 'button' : undefined"
              :aria-expanded="canChangeStatus ? showStatusMenu : undefined"
              :aria-controls="
                canChangeStatus ? `card-status-menu-${card.id}` : undefined
              "
              :aria-haspopup="canChangeStatus ? 'menu' : undefined"
              class="relative text-label-small font-medium px-1.5 py-0.5 rounded-md border flex items-center gap-1.5 after:absolute after:-inset-y-2 after:inset-x-0 focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="[
                statusClasses,
                canChangeStatus
                  ? 'cursor-pointer transition-opacity hover:opacity-80'
                  : 'cursor-default',
              ]"
              @click="handleStatusClick"
              @keydown.down.prevent.stop="
                canChangeStatus ? openStatusMenu('first') : null
              "
              @keydown.up.prevent.stop="
                canChangeStatus ? openStatusMenu('last') : null
              "
            >
              <span
                class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                :class="{
                  'bg-n-slate-9': cardStatus === 'open',
                  'bg-n-teal-9': cardStatus === 'won',
                  'bg-n-ruby-9': cardStatus === 'lost',
                }"
              />
              {{ statusLabel }}
              <FluentIcon
                v-if="canChangeStatus"
                icon="chevron-down"
                size="8"
                class="opacity-50"
              />
            </component>

            <!-- Status Dropdown -->
            <template #content>
              <div
                v-if="showStatusMenu"
                :id="`card-status-menu-${card.id}`"
                ref="statusDropdownRef"
                role="menu"
                :aria-labelledby="`card-status-trigger-${card.id}`"
                class="w-32 bg-n-solid-2 rounded-xl shadow-lg border border-n-weak py-1 origin-top-left"
                @click.stop
                @keydown="
                  event =>
                    handleMenuKeydown(event, statusButtonRef, closeStatusMenu)
                "
                @focusout="
                  event => closeAfterFocusLeaves(event, closeStatusMenu)
                "
              >
                <button
                  v-for="option in statusOptions"
                  :key="option.value"
                  type="button"
                  role="menuitem"
                  tabindex="-1"
                  class="w-full min-h-11 px-3 py-2 text-left text-label-small hover:bg-n-alpha-1 flex items-center gap-2 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                  @click.stop="changeStatus(option.value)"
                >
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="{
                      'bg-n-slate-9': option.value === 'open',
                      'bg-n-teal-9': option.value === 'won',
                      'bg-n-ruby-9': option.value === 'lost',
                    }"
                  />
                  <span class="text-n-slate-11">{{ option.label }}</span>
                </button>
              </div>
            </template>
          </Popover>
        </div>

        <!-- Agente — clicável, abre dropdown teleportado (busca + seleção +
             remover atribuição).
             O agente é o conteúdo elástico desta linha: encolhe e trunca. O
             badge de sequência tem flex-shrink-0 próprio e mantém o tamanho.
             Antes, quando havia sequência ativa, este bloco também recebia
             flex-shrink-0 — com os DOIS irredutíveis a linha estourava e o
             badge vazava ~53px para fora do card (report 2026-08-05). O
             min-w-0 só tem efeito se o item puder encolher. -->
        <div class="min-w-0 flex-1 [&>span]:flex [&>span]:w-full">
          <Popover
            ref="agentPopoverRef"
            align="start"
            disable-mobile-view
            :show-content-border="false"
            @hide="showAgentDropdown = false"
          >
            <button
              :id="`card-agent-trigger-${card.id}`"
              ref="agentButtonRef"
              v-tooltip="{
                content: agentInfo
                  ? t('PIPELINE.CARD.AGENT_DROPDOWN.CHANGE_TOOLTIP', {
                      name: agentInfo.name,
                    })
                  : t('PIPELINE.CARD.AGENT_DROPDOWN.ASSIGN_TOOLTIP'),
                delay: { show: 200, hide: 0 },
                placement: 'top',
              }"
              :aria-label="
                agentInfo
                  ? t('PIPELINE.CARD.AGENT_DROPDOWN.CHANGE_TOOLTIP', {
                      name: agentInfo.name,
                    })
                  : t('PIPELINE.CARD.AGENT_DROPDOWN.ASSIGN_TOOLTIP')
              "
              :aria-expanded="showAgentDropdown"
              :aria-controls="`card-agent-list-${card.id}`"
              aria-haspopup="listbox"
              class="relative flex w-full items-center gap-1.5 group/agent cursor-pointer rounded-md hover:bg-n-alpha-2 -mx-1 px-1 py-0.5 transition-colors min-w-0 after:absolute after:-inset-y-2 after:inset-x-0 focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="{ 'opacity-60 cursor-wait': isAssigningAgent }"
              type="button"
              :aria-busy="isAssigningAgent"
              :aria-disabled="isAssigningAgent"
              @click.stop="toggleAgentDropdown"
              @keydown.down.prevent.stop="openAgentDropdown"
              @keydown.up.prevent.stop="openAgentDropdown"
            >
              <template v-if="agentInfo">
                <span
                  v-if="agentInfo.thumbnail || agentInfo.avatar_url"
                  class="w-5 h-5 rounded-full overflow-hidden ring-1 ring-n-weak flex-shrink-0"
                >
                  <img
                    :src="agentInfo.thumbnail || agentInfo.avatar_url"
                    :alt="agentInfo.name"
                    class="w-full h-full object-cover"
                  />
                </span>
                <span
                  v-else
                  class="w-5 h-5 rounded-full bg-n-alpha-2 flex items-center justify-center ring-1 ring-n-weak flex-shrink-0"
                >
                  <span class="text-[10px] font-bold text-n-slate-10">
                    {{ agentInfo.name?.charAt(0)?.toUpperCase() || 'A' }}
                  </span>
                </span>
                <!-- Hide agent name in compact mode (active sequence + linked
                     conversation already crowd the footer; avatar + tooltip
                     keep the agent identifiable). -->
                <span
                  v-if="!isFooterCrowded"
                  class="text-label-small text-n-slate-10 truncate min-w-0 group-hover/agent:text-n-slate-12 transition-colors"
                >
                  {{ agentInfo.name }}
                </span>
              </template>
              <template v-else>
                <!-- UX A2 (2026-04-22): empty state era avatar dashed quase
                     invisível (opacity-40) sem texto — dava impressão de
                     card incompleto. Agora ícone person-add visível +
                     label italic dão affordance clara de "atribuir agente". -->
                <span
                  class="w-5 h-5 rounded-full bg-n-alpha-1 flex items-center justify-center border border-dashed border-n-slate-7 flex-shrink-0"
                >
                  <FluentIcon
                    icon="person-add"
                    size="12"
                    class="text-n-slate-10"
                  />
                </span>
                <!-- Mesma regra do nome do agente: com sequência ativa o
                     rodapé já está cheio e o rótulo truncava para uma única
                     letra ("N"), que não informa nada. O avatar tracejado e o
                     tooltip mantêm a affordance de atribuir. -->
                <span
                  v-if="!isFooterCrowded"
                  class="text-label-small text-n-slate-10 italic truncate min-w-0"
                >
                  {{ t('PIPELINE.CARD.NO_AGENT') }}
                </span>
              </template>
            </button>

            <!-- Agent Dropdown — UX 2026-04-22 -->
            <template #content>
              <div
                v-if="showAgentDropdown"
                :id="`card-agent-menu-${card.id}`"
                ref="agentDropdownRef"
                class="w-64 max-h-[280px] flex flex-col rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-bottom-left overflow-hidden"
                @click.stop
                @keydown.esc.stop.prevent="closeAgentDropdownAndRestoreFocus"
                @focusout="
                  event => closeAfterFocusLeaves(event, closeAgentDropdown)
                "
              >
                <!-- Search input -->
                <div class="p-2 border-b border-n-weak">
                  <input
                    :id="`card-agent-search-${card.id}`"
                    ref="agentSearchRef"
                    v-model="agentSearchQuery"
                    type="text"
                    role="combobox"
                    name="pipeline_card_agent_search"
                    autocomplete="off"
                    aria-autocomplete="list"
                    aria-expanded="true"
                    :aria-controls="`card-agent-list-${card.id}`"
                    :aria-label="
                      t('PIPELINE.CARD.AGENT_DROPDOWN.SEARCH_PLACEHOLDER')
                    "
                    :placeholder="
                      t('PIPELINE.CARD.AGENT_DROPDOWN.SEARCH_PLACEHOLDER')
                    "
                    class="min-h-11 w-full px-2 py-1.5 text-sm bg-n-alpha-1 text-n-slate-12 placeholder-n-slate-9 rounded-md focus-visible:outline-2 focus-visible:outline-n-brand focus:bg-n-alpha-2 transition-colors"
                    @click.stop
                    @keydown.down.prevent.stop="focusAgentOption('first')"
                    @keydown.up.prevent.stop="focusAgentOption('last')"
                    @keydown.esc.stop.prevent="
                      closeAgentDropdownAndRestoreFocus
                    "
                  />
                </div>
                <!-- Agents list (scrollable) -->
                <div
                  :id="`card-agent-list-${card.id}`"
                  ref="agentListRef"
                  role="listbox"
                  :aria-labelledby="`card-agent-trigger-${card.id}`"
                  class="flex-1 overflow-y-auto p-1"
                  @keydown="handleAgentListKeydown"
                >
                  <button
                    v-for="agent in filteredAgents"
                    :key="agent.id"
                    type="button"
                    role="option"
                    :aria-selected="agentInfo?.id === agent.id"
                    tabindex="-1"
                    class="w-full min-h-11 flex items-center gap-2 px-2 py-2 rounded-md text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    :class="{
                      'bg-n-alpha-2 font-semibold':
                        agentInfo && agentInfo.id === agent.id,
                    }"
                    @click.stop="handleSelectAgent(agent.id)"
                  >
                    <div
                      v-if="agent.thumbnail || agent.avatar_url"
                      class="w-6 h-6 rounded-full overflow-hidden ring-1 ring-n-weak flex-shrink-0"
                    >
                      <img
                        :src="agent.thumbnail || agent.avatar_url"
                        :alt="agent.name"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <div
                      v-else
                      class="w-6 h-6 rounded-full bg-n-alpha-2 flex items-center justify-center ring-1 ring-n-weak flex-shrink-0"
                    >
                      <span class="text-[10px] font-bold text-n-slate-10">
                        {{ agent.name?.charAt(0)?.toUpperCase() || 'A' }}
                      </span>
                    </div>
                    <span class="truncate flex-1">{{ agent.name }}</span>
                    <FluentIcon
                      v-if="agentInfo && agentInfo.id === agent.id"
                      icon="checkmark"
                      size="14"
                      class="text-n-brand flex-shrink-0"
                    />
                  </button>
                  <p
                    v-if="filteredAgents.length === 0"
                    class="text-center text-xs text-n-slate-9 italic py-3"
                  >
                    {{ t('PIPELINE.CARD.AGENT_DROPDOWN.NO_AGENTS_FOUND') }}
                  </p>
                </div>
                <!-- Unassign action (only when agent assigned) -->
                <div v-if="agentInfo" class="border-t border-n-weak p-1">
                  <button
                    type="button"
                    class="w-full min-h-11 flex items-center gap-2 px-2 py-2 rounded-md text-sm text-n-ruby-11 text-left hover:bg-n-ruby-3 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    @click.stop="handleUnassignAgent"
                  >
                    <FluentIcon icon="dismiss" size="14" />
                    {{ t('PIPELINE.CARD.AGENT_DROPDOWN.UNASSIGN') }}
                  </button>
                </div>
              </div>
            </template>
          </Popover>
        </div>

        <!-- Sequence Progress Badge (Phase 1.2)
             Visible when this card has an active or paused sequence.
             Click opens card details focused on the Sequences tab. -->
        <button
          v-if="activeSequenceProgress"
          v-tooltip="{
            content: sequenceTooltip,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          class="relative flex-shrink-0 flex items-center gap-1 text-label-small font-medium px-1.5 py-0.5 rounded-md transition-colors after:absolute after:-inset-y-2 after:inset-x-0 focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            activeSequenceProgress.status === 'active'
              ? 'bg-n-iris-3 text-n-iris-11 hover:bg-n-iris-4'
              : 'bg-n-amber-3 text-n-amber-11 hover:bg-n-amber-4'
          "
          @click.stop="handleOpenSequences"
        >
          <FluentIcon icon="flash-on" size="12" />
          <span
            >{{ activeSequenceProgress.current_step }}/{{
              activeSequenceProgress.total_steps
            }}</span
          >
          <!-- Mini progress bar -->
          <progress
            :value="progressPercent"
            max="100"
            class="h-1 w-8 appearance-none overflow-hidden rounded-full bg-black/10 [&::-webkit-progress-bar]:bg-black/10 [&::-webkit-progress-value]:rounded-full [&::-moz-progress-bar]:rounded-full"
            :class="
              activeSequenceProgress.status === 'active'
                ? '[&::-webkit-progress-value]:bg-n-iris-9 [&::-moz-progress-bar]:bg-n-iris-9'
                : '[&::-webkit-progress-value]:bg-n-amber-9 [&::-moz-progress-bar]:bg-n-amber-9'
            "
            :aria-label="t('PIPELINE.CARD.SEQUENCE_PROGRESS')"
          />
        </button>

        <!-- Conversation Link -->
        <button
          v-if="hasConversation"
          v-tooltip="{
            content: t('PIPELINE.CARD.CONVERSATION_BADGE_TOOLTIP', {
              id: card.conversation_display_id,
            }),
            delay: { show: 300, hide: 0 },
            placement: 'top',
          }"
          type="button"
          :aria-label="
            t('PIPELINE.CARD.CONVERSATION_BADGE_TOOLTIP', {
              id: card.conversation_display_id,
            })
          "
          class="relative flex flex-shrink-0 items-center gap-1 rounded-md bg-n-brand/10 px-1.5 py-0.5 text-n-brand transition-colors after:absolute after:-inset-y-2 after:inset-x-0 hover:bg-n-brand/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 motion-reduce:transition-none"
          @click.stop="openConversation"
        >
          <FluentIcon icon="chat" size="12" />
          <span class="text-label-small font-semibold">
            #{{ card.conversation_display_id }}
          </span>
        </button>
      </div>
    </div>
    <!-- Hidden details for screen readers -->
    <div :id="`card-details-${card.id}`" class="sr-only">
      {{ descriptionPreview }}
      {{
        agentInfo
          ? t('PIPELINE.CARD.ASSIGNED_TO', { name: agentInfo.name })
          : t('PIPELINE.CARD.NO_AGENT')
      }}
    </div>
    <!-- Fase 59 — histórico financeiro do card, aberto pelo chip de
         oportunidades. Só é montado quando pedido: o board renderiza centenas
         de cards e um modal por card seria peso morto.
         O `@click.stop` do invólucro NÃO é decorativo: o modal é montado aqui
         dentro, e o <article> inteiro é clicável (`@click="handleOpenDetails"`).
         Sem ele, clicar em "Estornar", em "Fechar" ou no próprio fundo do modal
         abre os detalhes do card e desmonta o modal no meio da operação —
         medido em chatdev, era o que tornava o estorno inalcançável pelo board.
         O invólucro só existe enquanto o modal existe (`v-if`), e o modal se
         posiciona por `fixed`, então ele não ocupa espaço no card. -->
    <div v-if="showOpportunityHistory" class="select-text" @click.stop>
      <OpportunityHistoryModal
        :card="card"
        :show="showOpportunityHistory"
        @close="showOpportunityHistory = false"
      />
    </div>
  </article>
</template>

<!-- CSS migrated to Tailwind classes -->
